import type { FileDetail, MovementRecord } from '@jmc/shared';
import { formatDate, formatDateTime } from '@jmc/shared';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { ErrorState, LoadingBlock, Mono } from '../components/ui.js';

/**
 * The entry form, printed back with the details filled in.
 *
 * The record room works off paper: the survey sheet goes into the file cover
 * so that whoever opens the bundle next can see what was recorded without a
 * computer. Until now the only printable page was the screen itself, which
 * prints as a web page -- navigation, panels, nothing like the ISO form the
 * section actually files.
 *
 * The QR square carries this file's page address. Scanned from a phone in the
 * record room, it opens the full record, shelf location included. It is a link
 * into the application, not the data itself: the record stays behind the same
 * sign-in as everywhere else, and a photographed cover does not leak a
 * citizen's details to whoever picks it up.
 */

interface Row {
  labelEn: string;
  labelHi: string;
  value: string | null | undefined;
  wide?: boolean;
  propertyOnly?: boolean;
}

export function FilePrintFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useI18n();
  const [qr, setQr] = useState<string | null>(null);

  // Deliberately the same request and the same cache key as the file's own
  // page, because the Print button is pressed from there: the record is
  // already in hand, so the sheet renders without a second round trip.
  //
  // It used to fetch /files/:id under that same key. Two different shapes
  // behind one key meant whichever screen loaded first won, and arriving from
  // the file page handed this one { file, movements } -- every field read as
  // undefined and the sheet printed blank, with the wrong form heading.
  const query = useQuery({
    queryKey: ['file', id],
    queryFn: () =>
      api<{ file: FileDetail; movements: MovementRecord[] }>(`/files/${id}/history`),
    enabled: Boolean(id),
  });

  const file = query.data?.file;

  useEffect(() => {
    if (!file) return;
    const target = `${window.location.origin}/files/${file.id}`;
    // Rendered as an SVG string rather than a canvas: it has to survive a
    // print at whatever resolution the section's printer runs at, and a
    // canvas at 128px prints as a blur.
    QRCode.toString(target, {
      type: 'svg',
      margin: 0,
      errorCorrectionLevel: 'M',
    })
      .then(setQr)
      .catch(() => setQr(null));
  }, [file]);

  // Print once the QR is drawn, so the sheet is never sent to the printer
  // with an empty square where the code should be. `?preview` opens the sheet
  // without the print dialog, for checking it on screen first.
  useEffect(() => {
    if (!file || qr === null || params.has('preview')) return;
    const timer = setTimeout(() => window.print(), 300);
    return () => clearTimeout(timer);
  }, [file, qr, params]);

  if (query.isLoading) return <LoadingBlock />;
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (!file) return null;

  const isProperty = file.kind === 'PROPERTY';

  const rows: Row[] = [
    { labelEn: 'Department Name', labelHi: 'विभाग का नाम', value: file.departmentName },
    { labelEn: 'Zone', labelHi: 'जोन', value: file.zoneName },
    { labelEn: 'File Number', labelHi: 'फाइल संख्या', value: file.fileNumber, wide: true },
    { labelEn: 'File Name', labelHi: 'फाइल का नाम', value: file.fileName, wide: true },
    { labelEn: 'File Subject', labelHi: 'फाइल विषय', value: file.subjectEn, wide: true },
    { labelEn: 'File Subject (Hindi)', labelHi: 'फाइल विषय (हिंदी)', value: file.subjectHi, wide: true },
    {
      labelEn: 'First Allottee Name',
      labelHi: 'प्रथम आवंटी का नाम',
      value: file.firstAllotteeEn,
      propertyOnly: true,
    },
    {
      labelEn: 'First Allottee Name (Hindi)',
      labelHi: 'प्रथम आवंटी का नाम (हिंदी)',
      value: file.firstAllotteeHi,
      propertyOnly: true,
    },
    { labelEn: 'Scheme Name', labelHi: 'योजना का नाम', value: file.schemeNameEn, propertyOnly: true },
    {
      labelEn: 'Scheme Name (Hindi)',
      labelHi: 'योजना का नाम (हिंदी)',
      value: file.schemeNameHi,
      propertyOnly: true,
    },
    { labelEn: 'Scheme No.', labelHi: 'योजना संख्या', value: file.schemeNo, propertyOnly: true },
    { labelEn: 'Society Name', labelHi: 'सोसायटी का नाम', value: file.societyName, propertyOnly: true },
    { labelEn: 'Sector', labelHi: 'सेक्टर', value: file.sector, propertyOnly: true },
    { labelEn: 'Plot No.', labelHi: 'भूखंड संख्या', value: file.plotNo, propertyOnly: true },
    { labelEn: 'House No.', labelHi: 'मकान संख्या', value: file.houseNo, propertyOnly: true },
    { labelEn: 'Lease A/C No.', labelHi: 'लीज खाता संख्या', value: file.leaseAcNo, propertyOnly: true },
    {
      labelEn: 'Address / Lease Description',
      labelHi: 'पता / लीज विवरण',
      value: file.leaseDescription,
      wide: true,
      propertyOnly: true,
    },
    { labelEn: 'Ward No.', labelHi: 'वार्ड संख्या', value: file.wardNo },
    { labelEn: 'Year', labelHi: 'वर्ष', value: file.financialYear },
    { labelEn: 'OIC Name', labelHi: 'ओ.आई.सी. नाम', value: file.oicName },
    { labelEn: 'Remark', labelHi: 'टिप्पणी', value: file.remark, wide: true },
  ].filter((row) => isProperty || !row.propertyOnly);

  // Where each row lands in the two-column grid. A full-width row resets the
  // count, so the dividing line falls between a genuine pair of cells rather
  // than on whichever row happens to be even -- and a left cell with no
  // partner beside it draws no line into the empty half.
  let column = 0;
  const placed = rows.map((row, index) => {
    if (row.wide) {
      column = 0;
      return { row, divider: false };
    }
    const isLeft = column === 0;
    const next = rows[index + 1];
    // A left cell with nothing to pair with takes the whole width, otherwise
    // the row's bottom border stops halfway across the sheet.
    const alone = isLeft && (next == null || next.wide);
    column = alone ? 0 : isLeft ? 1 : 0;
    return { row, divider: isLeft && !alone, span: alone };
  });

  return (
    <div className="print-sheet mx-auto max-w-[820px] bg-white">
      <div className="no-print mb-4 flex items-center justify-between gap-3">
        <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
          {t('action.back')}
        </button>
        <button type="button" className="btn-primary" onClick={() => window.print()}>
          {t('action.print')}
        </button>
      </div>

      <div className="border border-ink-900">
        {/* Header: who the sheet belongs to, which form it is, and the code. */}
        <div className="flex items-start justify-between gap-4 border-b border-ink-900 px-4 py-3">
          <div>
            <h1 className="text-[1.0625rem] font-bold leading-snug">
              नगर निगम जयपुर
              <span className="block text-[0.8125rem] font-semibold text-ink-700">
                Nagar Nigam Jaipur — Record Room
              </span>
            </h1>
            <p className="mt-1 text-[0.8125rem] font-medium">
              {isProperty
                ? 'ISO Form 2 — पट्टा / सम्पत्ति फाइल विवरण (Property File Details)'
                : 'ISO Form 1 — सामान्य फाइल विवरण (General File Details)'}
            </p>
          </div>

          <div className="shrink-0 text-center">
            {qr ? (
              <div
                className="h-[86px] w-[86px] [&>svg]:h-full [&>svg]:w-full"
                dangerouslySetInnerHTML={{ __html: qr }}
                aria-label="QR code linking to this record"
              />
            ) : (
              <div className="h-[86px] w-[86px] border border-dashed border-line-strong" />
            )}
            <p className="mt-1 text-[0.625rem] leading-tight text-ink-500">
              स्कैन करें / Scan
              <br />
              for full record
            </p>
          </div>
        </div>

        {/* The shelf address, boxed on its own: it is what the whole system
            exists to answer, and on paper it has to be findable at a glance. */}
        <div className="grid grid-cols-5 border-b border-ink-900 text-center">
          {[
            ['e-File No. / ई-फाइल सं.', file.eFileNumber],
            ['Room / कक्ष', file.location?.roomCode ?? '—'],
            ['Almirah / अलमारी', file.location?.rackCode ?? '—'],
            ['Column / कॉलम', file.location?.columnCode ?? '—'],
            ['Bag / बैग', file.location?.bagCode ?? '—'],
          ].map(([label, value], index) => (
            <div
              key={label}
              className={index < 4 ? 'border-r border-ink-900 px-2 py-2' : 'px-2 py-2'}
            >
              <p className="text-[0.625rem] uppercase tracking-wide text-ink-500">{label}</p>
              <Mono className="text-[0.9375rem] font-bold">{value}</Mono>
            </div>
          ))}
        </div>

        <dl className="grid grid-cols-2">
          {placed.map(({ row, divider, span }, index) => (
            <div
              key={row.labelEn}
              className={[
                'border-ink-900 px-3 py-2',
                row.wide || span ? 'col-span-2' : '',
                index < placed.length - 1 ? 'border-b' : '',
                divider ? 'border-r' : '',
              ].join(' ')}
            >
              <dt className="text-[0.6875rem] text-ink-500">
                {row.labelHi} / {row.labelEn}
              </dt>
              <dd className="min-h-[1.25rem] text-[0.875rem] font-medium">
                {row.value?.trim() ? row.value : '—'}
              </dd>
            </div>
          ))}
        </dl>

        <div className="grid grid-cols-3 border-t border-ink-900 text-[0.6875rem]">
          <div className="border-r border-ink-900 px-3 py-2">
            <p className="text-ink-500">स्थिति / Status</p>
            <p className="font-medium">{t(`status.${file.status}`)}</p>
          </div>
          <div className="border-r border-ink-900 px-3 py-2">
            <p className="text-ink-500">दर्ज / Entered</p>
            <p className="font-medium">{formatDate(file.createdAt)}</p>
          </div>
          <div className="px-3 py-2">
            <p className="text-ink-500">अंतिम संशोधन / Last updated</p>
            <p className="font-medium">{formatDateTime(file.updatedAt)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 px-4 pb-4 pt-10 text-[0.75rem]">
          <p>प्रविष्टि करने वाला / Entered by ________________________</p>
          <p>अभिलेख अधिकारी / Record Officer ________________________</p>
        </div>
      </div>
    </div>
  );
}
